/**
 * Umplanen/Ersatz (G-22): Zeitraum + Pflicht-Mitarbeiter (dessen Planzeilen),
 * optional Schicht-Filter; Ersatz = Stellenzuordnung eines Ersatz-Mitarbeiters
 * und/oder neue Schicht. Ergebnis: updated-Zaehler + Konfliktliste (statt
 * Ninox-Alert-Ketten).
 */

import { useEffect, useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import {
  Sheet,
  SHEET_WIDTH,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select } from "@/components/ui/select"
import { fmtDate } from "@/lib/format"
import { todayIso } from "@/lib/isoWeek"
import { api, apiError } from "@/services/api"
import type { ReassignResponse } from "@/types"

interface ReassignSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function ReassignSheet({ open, onOpenChange }: ReassignSheetProps) {
  const queryClient = useQueryClient()
  const [from, setFrom] = useState(todayIso())
  const [to, setTo] = useState(todayIso())
  const [employeeId, setEmployeeId] = useState("")
  const [shiftId, setShiftId] = useState("")
  const [substituteId, setSubstituteId] = useState("")
  const [newPositionId, setNewPositionId] = useState("")
  const [newShiftId, setNewShiftId] = useState("")
  const [result, setResult] = useState<ReassignResponse | null>(null)

  useEffect(() => {
    if (open) {
      setEmployeeId("")
      setShiftId("")
      setSubstituteId("")
      setNewPositionId("")
      setNewShiftId("")
      setResult(null)
    }
  }, [open])

  const employees = useQuery({
    queryKey: ["employees", { size: 100 }],
    queryFn: () => api.employees.list({ size: 100 }),
    enabled: open,
  })

  const shifts = useQuery({
    queryKey: ["shifts", { size: 100 }],
    queryFn: () => api.organization.shifts.list({ size: 100 }),
    enabled: open,
  })

  // Ersatz-Stellenzuordnungen: employee_positions des gewaehlten Ersatz-Mitarbeiters
  const substitutePositions = useQuery({
    queryKey: ["employee", substituteId, "positions"],
    queryFn: () => api.employees.positions.list(substituteId),
    enabled: open && substituteId !== "",
  })

  const valid =
    employeeId !== "" &&
    from !== "" &&
    to !== "" &&
    from <= to &&
    // Ohne Ersatz-Stellenzuordnung muss zumindest eine neue Schicht gewaehlt sein
    (newPositionId !== "" || newShiftId !== "")

  const reassign = useMutation({
    mutationFn: () =>
      api.planning.reassign({
        from,
        to,
        employeeId: Number(employeeId),
        shiftId: shiftId ? Number(shiftId) : null,
        newPositionId: newPositionId ? Number(newPositionId) : null,
        newShiftId: newShiftId ? Number(newShiftId) : null,
      }),
    onSuccess: (r) => {
      setResult(r)
      queryClient.invalidateQueries({ queryKey: ["shiftPlan"] })
      queryClient.invalidateQueries({ queryKey: ["attendance"] })
      if (r.conflicts.length > 0) {
        toast.warning(
          `${r.updated} Planzeilen umgeplant, ${r.conflicts.length} Konflikt(e) übersprungen`
        )
      } else {
        toast.success(`${r.updated} Planzeilen umgeplant`)
      }
    },
    onError: (e) => toast.error(apiError(e)),
  })

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent sheetKey="reassign" defaultWidth={SHEET_WIDTH.lg} className="overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Umplanen / Ersatz</SheetTitle>
          <SheetDescription>
            Plant die Zeilen des Mitarbeiters im Zeitraum um; abwesende Tage werden
            übersprungen und als Konflikte gelistet (G-22).
          </SheetDescription>
        </SheetHeader>
        <div className="grid gap-4 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Von *</Label>
              <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Bis *</Label>
              <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Mitarbeiter (dessen Planzeilen) *</Label>
            <Select value={employeeId} onChange={(e) => setEmployeeId(e.target.value)}>
              <option value="">— Mitarbeiter wählen —</option>
              {(employees.data?.data ?? []).map((emp) => (
                <option key={emp.id} value={emp.id}>
                  {emp.displayName}
                </option>
              ))}
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label>Schicht-Filter (optional)</Label>
            <Select value={shiftId} onChange={(e) => setShiftId(e.target.value)}>
              <option value="">Alle Schichten</option>
              {(shifts.data?.data ?? []).map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </Select>
          </div>

          <div className="rounded-md border p-3">
            <p className="mb-3 text-sm font-medium">Ersatz</p>
            <div className="grid gap-3">
              <div className="space-y-1.5">
                <Label>Ersatz-Mitarbeiter</Label>
                <Select
                  value={substituteId}
                  onChange={(e) => {
                    setSubstituteId(e.target.value)
                    setNewPositionId("")
                  }}
                >
                  <option value="">— Ersatz wählen —</option>
                  {(employees.data?.data ?? [])
                    .filter((emp) => emp.id !== employeeId)
                    .map((emp) => (
                      <option key={emp.id} value={emp.id}>
                        {emp.displayName}
                      </option>
                    ))}
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Ersatz-Stellenzuordnung</Label>
                <Select
                  value={newPositionId}
                  disabled={substituteId === ""}
                  onChange={(e) => setNewPositionId(e.target.value)}
                >
                  <option value="">— Stellenzuordnung wählen —</option>
                  {(substitutePositions.data ?? []).map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.positionName ?? `#${p.positionId}`}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Neue Schicht (optional)</Label>
                <Select value={newShiftId} onChange={(e) => setNewShiftId(e.target.value)}>
                  <option value="">— Schicht behalten —</option>
                  {(shifts.data?.data ?? []).map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </Select>
              </div>
            </div>
          </div>

          {result && (
            <div className="rounded-md border bg-muted/40 p-3 text-sm">
              <p className="font-medium">{result.updated} Planzeilen umgeplant</p>
              {result.conflicts.length > 0 && (
                <ul className="mt-2 space-y-1 text-muted-foreground">
                  {result.conflicts.map((c, i) => (
                    <li key={`${c.date}-${i}`}>
                      {fmtDate(c.date)}: {c.reason}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
        <SheetFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Schließen
          </Button>
          <Button disabled={!valid || reassign.isPending} onClick={() => reassign.mutate()}>
            Umplanen
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
